import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Flex } from '@adobe/react-spectrum';
import './Privacy.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { injectConsentLinks, NITRO_CONSENT_LINK_ID } from '../../utils/consent';
import { FEATURE_FLAGS } from '../../config/featureFlags';

/**
 * Privacy Policy page. Required by Google in order to serve ads via NitroPay,
 * and the destination for the CCPA "Do Not Sell or Share" + GDPR consent links.
 *
 * The GDPR consent button and CCPA "Do Not Sell" link are INJECTED by NitroPay
 * into the placeholder elements below (`#ncmp-consent-link` / `[data-ccpa-link]`)
 * for users in the relevant regions — see src/utils/consent.js.
 *
 */

const LAST_UPDATED = 'June 1, 2026';
const CONTACT_EMAIL = 'questions@commandersalt.com';

export function Privacy() {
    const helmetName = 'Privacy Policy';
    const helmetCanonical = 'https://www.commandersalt.com/privacy';
    const helmetDescription =
        'Commandersalt.com privacy policy: what we collect, how we use and share it, our advertising and analytics partners, and your privacy choices and rights.';
    const helmetTitle = 'Commandersalt.com - Privacy Policy';

    // Footer links deep-link into specific sections (#ccpa, #consent). React
    // Router doesn't scroll to hash anchors on its own, so do it here.
    const location = useLocation();
    useEffect(() => {
        if (!location.hash) {
            return;
        }
        const el = document.getElementById(location.hash.slice(1));
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [location]);

    // Ask NitroPay to inject its consent controls into the placeholders below.
    // The ad script loads async, so retry for a few seconds after mount; each
    // call is a safe no-op until the relevant CMP/USP API is ready (and for
    // users outside the regions those controls apply to).
    useEffect(() => {
        if (!FEATURE_FLAGS.ads) {
            return undefined;
        }
        injectConsentLinks();
        let tries = 0;
        const handle = setInterval(() => {
            tries += 1;
            injectConsentLinks();
            if (tries >= 6) {
                clearInterval(handle);
            }
        }, 1000);
        return () => clearInterval(handle);
    }, []);

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />

            <div className="privacyRoot">
                <section className="privacyHero">
                    <span className="privacyHeroEyebrow">Legal</span>
                    <h1 className="privacyHeroTitle">Privacy Policy</h1>
                    <p className="privacyHeroSubtitle">
                        This policy explains what information Commandersalt.com collects, how we use and share it, who
                        our {FEATURE_FLAGS.ads ? 'advertising and analytics' : 'analytics'} partners are, and the
                        choices and rights you have over your data.
                    </p>
                    <span className="privacyUpdated">Last updated: {LAST_UPDATED}</span>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">1. Who we are</h2>
                    <p className="privacyCardBody">
                        Commandersalt.com (“Commandersalt”, “the site”, “we”, “us”, or “our”) is an independently
                        operated site, based in the United States, for analyzing Magic: The Gathering Commander/EDH
                        decks. We are the controller of the personal information described in this policy, and this
                        policy is governed by the laws of the United States. For any privacy question or request,
                        contact us at{' '}
                        <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                            {CONTACT_EMAIL}
                        </a>
                        .
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">2. Information we collect</h2>
                    <ul className="privacyList">
                        <li>
                            <strong>Account information.</strong> If you sign in, the email address and account
                            identifier provided through our authentication provider (AWS Cognito), plus any profile
                            details you choose to add.
                        </li>
                        <li>
                            <strong>Content you submit.</strong> Decklists, deck URLs, linked-account handles, and
                            related data you import, save, or generate on the site.
                        </li>
                        <li>
                            <strong>Usage &amp; device data.</strong> Pages viewed, features used, referring pages,
                            approximate location (derived from IP), IP address, browser and device type, and similar
                            diagnostic data, collected through cookies and comparable technologies.
                        </li>
                        {FEATURE_FLAGS.ads && (
                            <li>
                                <strong>Advertising data.</strong> Our advertising partners may collect cookie IDs,
                                device identifiers, and interaction data to serve and measure ads, as described in the
                                Advertising section below.
                            </li>
                        )}
                    </ul>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">3. How we use your information</h2>
                    <p className="privacyCardBody">We use the information we collect to:</p>
                    <ul className="privacyList">
                        <li>operate, maintain, and secure the site and your account;</li>
                        <li>provide deck analysis, leaderboards, and other features you request;</li>
                        <li>understand how the site is used so we can fix issues and improve it;</li>
                        {FEATURE_FLAGS.ads && <li>display and measure advertising that helps fund the site;</li>}
                        <li>detect, prevent, and respond to fraud, abuse, and security incidents; and</li>
                        <li>comply with our legal obligations and enforce our terms.</li>
                    </ul>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">4. Legal bases for processing (EEA / UK)</h2>
                    <p className="privacyCardBody">
                        If you are in the European Economic Area or the United Kingdom, we rely on the following legal
                        bases: <strong>performance of a contract</strong> (to provide the site and your account);{' '}
                        <strong>legitimate interests</strong> (to secure, analyze, and improve the site, balanced
                        against your rights);
                        {FEATURE_FLAGS.ads ? ' your ' : ' and your '}
                        <strong>consent</strong> (for non-essential cookies
                        {FEATURE_FLAGS.ads ? ' and personalized advertising' : ''}, which you can withdraw at any time);
                        and <strong>legal obligation</strong> where processing is required by law.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">5. How we share information</h2>
                    <p className="privacyCardBody">
                        We do not sell your personal information for money. We share it only as described here:
                    </p>
                    <ul className="privacyList">
                        <li>
                            <strong>Service providers.</strong> Vendors that run the site on our behalf — including
                            Amazon Web Services (hosting and Cognito authentication) and Google/Firebase (analytics).
                        </li>
                        {FEATURE_FLAGS.ads && (
                            <li>
                                <strong>Advertising partners.</strong> NitroPay and the ad exchanges and demand partners
                                it works with (including Google), which may receive cookie/device identifiers and usage
                                signals to serve and measure ads. Under some laws this sharing may be considered a
                                “sale” or “sharing” — see your rights below.
                            </li>
                        )}
                        <li>
                            <strong>Legal &amp; safety.</strong> When we believe disclosure is necessary to comply with
                            law, enforce our terms, or protect the rights, property, or safety of our users or the
                            public.
                        </li>
                        <li>
                            <strong>Business transfers.</strong> In connection with a merger, acquisition, or sale of
                            assets, subject to this policy.
                        </li>
                    </ul>
                </section>

                {FEATURE_FLAGS.ads && (
                    <section className="privacyCard">
                        <h2 className="privacyCardTitle">6. Advertising &amp; your ad choices</h2>
                        <p className="privacyCardBody">
                            We work with NitroPay to display advertising. NitroPay and its partners — including Google
                            and other ad exchanges — may use cookies, device identifiers, and similar technologies to
                            serve and measure ads, including personalized (interest-based) ads. In regions that require
                            it, ads are served according to your consent choices via NitroPay’s Consent Management
                            Platform.
                        </p>
                        <p className="privacyCardBody">
                            You can opt out of personalized advertising from Google at{' '}
                            <a
                                className="privacyLink"
                                href="https://adssettings.google.com"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Google Ads Settings
                            </a>
                            , and from participating vendors at{' '}
                            <a
                                className="privacyLink"
                                href="https://optout.aboutads.info"
                                target="_blank"
                                rel="noreferrer"
                            >
                                aboutads.info
                            </a>{' '}
                            and{' '}
                            <a
                                className="privacyLink"
                                href="https://www.youronlinechoices.eu"
                                target="_blank"
                                rel="noreferrer"
                            >
                                youronlinechoices.eu
                            </a>
                            . NitroPay’s practices are described in the{' '}
                            <a
                                className="privacyLink"
                                href="https://nitropay.com/privacy"
                                target="_blank"
                                rel="noreferrer"
                            >
                                NitroPay Privacy Policy
                            </a>
                            , and Google’s in{' '}
                            <a
                                className="privacyLink"
                                href="https://policies.google.com/privacy"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Google’s Privacy Policy
                            </a>
                            . Supporting us on Patreon removes ads entirely.
                        </p>
                    </section>
                )}

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">
                        {FEATURE_FLAGS.ads ? '7' : '6'}. Cookies &amp; similar technologies
                    </h2>
                    <p className="privacyCardBody">
                        We and our partners use cookies and similar technologies for essential site functionality
                        {FEATURE_FLAGS.ads ? ', analytics, and advertising' : ' and analytics'}. Essential cookies are
                        required for the site to work; others are used only as permitted by your choices. You can manage
                        or block cookies through your browser settings
                        {FEATURE_FLAGS.ads ? ', and adjust advertising consent through the controls below' : ''}.
                        Disabling some cookies may affect how the site works.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">{FEATURE_FLAGS.ads ? '8' : '7'}. Your privacy rights</h2>
                    <p className="privacyCardBody">
                        Depending on where you live, you may have the right to access, correct, delete, or port your
                        personal information, to object to or restrict certain processing, and to withdraw consent. To
                        exercise any of these rights, email us at{' '}
                        <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                            {CONTACT_EMAIL}
                        </a>
                        . We will not discriminate against you for exercising your rights.
                        {FEATURE_FLAGS.ads
                            ? ' Advertising consent and opt-out choices are handled through the consent controls below.'
                            : ''}
                    </p>
                    <p className="privacyCardBody">
                        <strong>Do Not Track &amp; Global Privacy Control.</strong> Because there is no finalized common
                        standard for “Do Not Track” (DNT) browser signals, our site does not currently respond to them.
                        {FEATURE_FLAGS.ads
                            ? ' To opt out of the “sale” or “sharing” of your personal information, use the “Do Not Sell or Share” control in the California section below or contact us.'
                            : ''}
                    </p>
                </section>

                {FEATURE_FLAGS.ads && (
                    <section className="privacyCard" id="consent">
                        <h2 className="privacyCardTitle">9. EEA / UK consent (GDPR)</h2>
                        <p className="privacyCardBody">
                            If you are in the EEA, the UK, or another region that requires consent, we ask for your
                            consent before storing or accessing non-essential cookies or processing personal data for
                            personalized advertising. You can review or change those choices at any time using the
                            control below.
                        </p>
                        {/* NitroPay injects its consent-management button here for EU/UK users. */}
                        <div id={NITRO_CONSENT_LINK_ID} className="privacyConsentControl" />
                    </section>
                )}

                {FEATURE_FLAGS.ads && (
                    <section className="privacyCard" id="ccpa">
                        <h2 className="privacyCardTitle">10. California privacy rights (CCPA / CPRA)</h2>
                        <p className="privacyCardBody">
                            If you are a California resident, the CCPA/CPRA gives you the right to know what personal
                            information we collect and how it is used and shared, to request access to or deletion of
                            your personal information, to correct inaccurate information, and to opt out of the “sale”
                            or “sharing” of your personal information.
                        </p>

                        <h3 className="privacyCardSubtitle">Categories of personal information we collect</h3>
                        <p className="privacyCardBody">
                            In the preceding 12 months we have collected the following statutory categories of personal
                            information:
                        </p>
                        <table className="privacyTable">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Collected</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>A. Identifiers (e.g. email, account ID, IP address, cookie/device IDs)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>B. California customer records (Cal. Civ. Code § 1798.80(e)) — e.g. email</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>C. Protected classification characteristics</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>D. Commercial information (purchase/transaction history)</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>E. Biometric information</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>F. Internet or other electronic network activity (browsing, interactions)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>G. Geolocation data (approximate, derived from IP)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>H. Sensory data (audio, visual, etc.)</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>I. Professional or employment-related information</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>J. Non-public education information (FERPA)</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>K. Inferences drawn from the above (e.g. ad-interest profiles)</td>
                                    <td>Yes</td>
                                </tr>
                            </tbody>
                        </table>

                        <h3 className="privacyCardSubtitle">“Sale” or “sharing” of personal information</h3>
                        <p className="privacyCardBody">
                            To fund the site, we and our advertising partners use cookies, device identifiers, and
                            similar technologies to deliver and measure ads. Under California law, this may be
                            considered a “sale” or “sharing” of personal information. In the preceding 12 months, the
                            categories involved in this sharing are: <strong>A (identifiers)</strong>,{' '}
                            <strong>F (network activity)</strong>, <strong>G (geolocation)</strong>, and{' '}
                            <strong>K (inferences)</strong>. We do not knowingly sell or share the personal information
                            of consumers under 16, and we do not sell your information for money.
                        </p>
                        <p className="privacyCardBody">
                            To opt out, use the control below, or email us at{' '}
                            <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                                {CONTACT_EMAIL}
                            </a>
                            .
                        </p>
                        {/* NitroPay injects the "Do Not Sell or Share" link here for California residents. */}
                        <span data-ccpa-link="1" className="privacyConsentControl" />

                        <h3 className="privacyCardSubtitle">Exercising your rights</h3>
                        <p className="privacyCardBody">
                            To exercise your access, portability, correction, or deletion rights, submit a verifiable
                            request by emailing{' '}
                            <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                                {CONTACT_EMAIL}
                            </a>
                            . So we can verify your identity, please give us enough information to reasonably confirm
                            you are the person we collected information about (or an authorized agent), and describe
                            your request with enough detail for us to evaluate it. You may make an access or portability
                            request up to twice per 12-month period, and you may use an authorized agent or act on
                            behalf of your minor child. We will not discriminate against you for exercising these
                            rights.
                        </p>
                    </section>
                )}

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">{FEATURE_FLAGS.ads ? '11' : '8'}. Data retention</h2>
                    <p className="privacyCardBody">
                        We keep personal information only as long as needed for the purposes described in this policy —
                        for example, account data for as long as your account is active — and then delete or anonymize
                        it, unless a longer period is required by law. You can request deletion of your account data at
                        any time.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">{FEATURE_FLAGS.ads ? '12' : '9'}. Data security</h2>
                    <p className="privacyCardBody">
                        We use reasonable technical and organizational measures to protect your information. No method
                        of transmission or storage is completely secure, however, and we cannot guarantee absolute
                        security.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">
                        {FEATURE_FLAGS.ads ? '13' : '10'}. International data transfers
                    </h2>
                    <p className="privacyCardBody">
                        We operate in the United States, and our service providers may process your information in the
                        United States or other countries. Those providers process data under their own data-processing
                        terms, which include safeguards for international transfers where required.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">{FEATURE_FLAGS.ads ? '14' : '11'}. Children’s privacy</h2>
                    <p className="privacyCardBody">
                        The site is not directed to children under 13 (or the applicable age in your jurisdiction), and
                        we do not knowingly collect personal information from them. If you believe a child has provided
                        us personal information, contact us and we will delete it.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">{FEATURE_FLAGS.ads ? '15' : '12'}. Changes to this policy</h2>
                    <p className="privacyCardBody">
                        We may update this policy from time to time. Material changes will be reflected by updating the
                        “Last updated” date above and, where appropriate, by a more prominent notice.
                    </p>
                </section>

                <section className="privacyCard">
                    <h2 className="privacyCardTitle">{FEATURE_FLAGS.ads ? '16' : '13'}. Contact us</h2>
                    <p className="privacyCardBody">
                        Questions, requests, or complaints about this policy or your data? Email{' '}
                        <a className="privacyLink" href={`mailto:${CONTACT_EMAIL}`}>
                            {CONTACT_EMAIL}
                        </a>
                        .
                    </p>
                </section>
            </div>
        </Flex>
    );
}
